import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, AlertTriangle, Calendar, Brain, Target, Settings } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import subscriptionService from '../../services/subscriptionService';
import symptomService from '../../services/symptomService';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import PatternRecognitionPanel from './components/PatternRecognitionPanel';
import CorrelationMatrix from './components/CorrelationMatrix';
import PredictiveInsights from './components/PredictiveInsights';
import TriggerAnalysis from './components/TriggerAnalysis';
import SeasonalCorrelations from './components/SeasonalCorrelations';
import InterventionEffectiveness from './components/InterventionEffectiveness';

import AnalyticsFilters from './components/AnalyticsFilters';
import CloseButton from '../../components/CloseButton';
import LogoutButton from '../../components/LogoutButton';

const AdvancedSymptomAnalytics = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [symptomData, setSymptomData] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [filters, setFilters] = useState({
    timeframe: '90days',
    symptomTypes: [],
    severityThreshold: 1,
    includeWeather: true,
    includeMedication: true
  });
  const [activePanel, setActivePanel] = useState('patterns');

  useEffect(() => {
    checkAccessAndLoadData();
  }, []);

  useEffect(() => {
    if (hasAccess && symptomData?.length > 0) {
      generateAnalytics();
    }
  }, [filters, symptomData]);

  const checkAccessAndLoadData = async () => {
    setLoading(true);
    try {
      // Check premium access
      const { hasAccess: premiumAccess } = await subscriptionService?.checkFeatureAccess('advanced_analytics');
      
      if (!premiumAccess) {
        navigate('/feature-gate-modal', { 
          state: { 
            feature: 'Advanced Symptom Analytics',
            requiredPlan: 'Premium'
          } 
        });
        return;
      }

      setHasAccess(true);

      // Load symptom data
      const { data: symptoms, error } = await symptomService?.getEntries();
      if (!error && symptoms) {
        setSymptomData(symptoms);
      }
    } catch (error) {
      console.error('Failed to load analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateAnalytics = () => {
    // Advanced analytics generation
    const analytics = {
      patterns: detectPatterns(symptomData, filters),
      correlations: calculateCorrelations(symptomData, filters),
      predictions: generatePredictions(symptomData, filters),
      triggers: analyzeTriggers(symptomData, filters),
      seasonal: analyzeSeasonalPatterns(symptomData, filters),
      interventions: evaluateInterventions(symptomData, filters)
    };

    setAnalyticsData(analytics);
  };

  const detectPatterns = (data, filters) => {
    // Pattern recognition algorithm
    const timeframeData = filterByTimeframe(data, filters?.timeframe);
    const patterns = [];

    // Frequency patterns
    const symptomFrequency = {};
    timeframeData?.forEach(entry => {
      entry?.symptom_categories?.forEach(symptom => {
        symptomFrequency[symptom] = (symptomFrequency?.[symptom] || 0) + 1;
      });
    });

    // Time-of-day patterns
    const timePatterns = {};
    timeframeData?.forEach(entry => {
      const hour = new Date(entry?.created_at)?.getHours();
      const timeSlot = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
      timePatterns[timeSlot] = (timePatterns?.[timeSlot] || 0) + 1;
    });

    // Severity trends
    const severityTrend = timeframeData?.map(entry => ({
      date: entry?.created_at,
      severity: entry?.severity
    }));

    patterns?.push({
      type: 'frequency',
      data: symptomFrequency,
      confidence: 0.85,
      insight: `Most frequent symptom: ${Object.keys(symptomFrequency)?.sort((a, b) => symptomFrequency?.[b] - symptomFrequency?.[a])?.[0]}`
    });

    patterns?.push({
      type: 'timing',
      data: timePatterns,
      confidence: 0.78,
      insight: `Symptoms most common in ${Object.keys(timePatterns)?.sort((a, b) => timePatterns?.[b] - timePatterns?.[a])?.[0]}`
    });

    patterns?.push({
      type: 'severity_trend',
      data: severityTrend,
      confidence: 0.82,
      insight: calculateTrendInsight(severityTrend)
    });

    return patterns;
  };

  const calculateCorrelations = (data, filters) => {
    const timeframeData = filterByTimeframe(data, filters?.timeframe);
    const correlations = [];

    // Symptom co-occurrence
    const coOccurrence = {};
    timeframeData?.forEach(entry => {
      const symptoms = entry?.symptom_categories || [];
      for (let i = 0; i < symptoms?.length; i++) {
        for (let j = i + 1; j < symptoms?.length; j++) {
          const pair = [symptoms?.[i], symptoms?.[j]]?.sort()?.join(' + ');
          coOccurrence[pair] = (coOccurrence?.[pair] || 0) + 1;
        }
      }
    });

    // Trigger correlations
    const triggerCorrelations = {};
    timeframeData?.forEach(entry => {
      entry?.triggers?.forEach(trigger => {
        if (!triggerCorrelations?.[trigger]) {
          triggerCorrelations[trigger] = { count: 0, avgSeverity: 0, totalSeverity: 0 };
        }
        triggerCorrelations[trigger].count++;
        triggerCorrelations[trigger].totalSeverity += entry?.severity || 0;
        triggerCorrelations[trigger].avgSeverity = triggerCorrelations?.[trigger]?.totalSeverity / triggerCorrelations?.[trigger]?.count;
      });
    });

    Object.entries(coOccurrence)?.forEach(([pair, count]) => {
      if (count >= 3) {
        correlations?.push({
          type: 'symptom_pair',
          items: pair,
          frequency: count,
          significance: count / timeframeData?.length,
          confidence: Math.min(0.95, count / 10)
        });
      }
    });

    Object.entries(triggerCorrelations)?.forEach(([trigger, stats]) => {
      if (stats?.count >= 2) {
        correlations?.push({
          type: 'trigger',
          trigger,
          frequency: stats?.count,
          avgSeverity: stats?.avgSeverity,
          significance: stats?.avgSeverity / 10,
          confidence: Math.min(0.90, stats?.count / 8)
        });
      }
    });

    return correlations;
  };

  const generatePredictions = (data, filters) => {
    const timeframeData = filterByTimeframe(data, filters?.timeframe);
    const predictions = [];

    // Escalation risk prediction
    const recentEntries = timeframeData?.slice(-7);
    const avgRecentSeverity = recentEntries?.reduce((sum, e) => sum + (e?.severity || 0), 0) / recentEntries?.length;
    const overallAvgSeverity = timeframeData?.reduce((sum, e) => sum + (e?.severity || 0), 0) / timeframeData?.length;

    if (avgRecentSeverity > overallAvgSeverity * 1.3) {
      predictions?.push({
        type: 'escalation_risk',
        risk: 'high',
        confidence: 0.75,
        message: 'Symptom severity increasing - consider medical consultation',
        timeframe: '7-14 days',
        recommendation: 'Schedule appointment with healthcare provider'
      });
    }

    // Pattern-based predictions
    const dayOfWeekPattern = {};
    timeframeData?.forEach(entry => {
      const day = new Date(entry?.created_at)?.getDay();
      dayOfWeekPattern[day] = (dayOfWeekPattern?.[day] || 0) + 1;
    });

    const peakDay = Object.entries(dayOfWeekPattern)?.sort((a, b) => b?.[1] - a?.[1])?.[0];
    if (peakDay && peakDay?.[1] >= 3) {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      predictions?.push({
        type: 'weekly_pattern',
        risk: 'medium',
        confidence: 0.68,
        message: `Symptoms more likely on ${dayNames?.[peakDay?.[0]]}`,
        timeframe: 'Weekly',
        recommendation: 'Plan preventive measures for this day'
      });
    }

    return predictions;
  };

  const analyzeTriggers = (data, filters) => {
    const timeframeData = filterByTimeframe(data, filters?.timeframe);
    const triggerAnalysis = {};

    timeframeData?.forEach(entry => {
      entry?.triggers?.forEach(trigger => {
        if (!triggerAnalysis?.[trigger]) {
          triggerAnalysis[trigger] = {
            count: 0,
            severities: [],
            avgSeverity: 0,
            maxSeverity: 0,
            symptoms: {}
          };
        }
        triggerAnalysis[trigger].count++;
        triggerAnalysis?.[trigger]?.severities?.push(entry?.severity || 0);
        triggerAnalysis[trigger].maxSeverity = Math.max(triggerAnalysis?.[trigger]?.maxSeverity, entry?.severity || 0);
        
        entry?.symptom_categories?.forEach(symptom => {
          triggerAnalysis[trigger].symptoms[symptom] = (triggerAnalysis?.[trigger]?.symptoms?.[symptom] || 0) + 1;
        });
      });
    });

    Object.keys(triggerAnalysis)?.forEach(trigger => {
      const stats = triggerAnalysis?.[trigger];
      stats.avgSeverity = stats?.severities?.reduce((a, b) => a + b, 0) / stats?.severities?.length;
      stats.impact = stats?.avgSeverity * (stats?.count / timeframeData?.length);
    });

    return Object.entries(triggerAnalysis)
      ?.map(([trigger, stats]) => ({ trigger, ...stats }))
      ?.sort((a, b) => b?.impact - a?.impact);
  };

  const analyzeSeasonalPatterns = (data, filters) => {
    const seasonalData = {};
    
    data?.forEach(entry => {
      const month = new Date(entry?.created_at)?.getMonth();
      const season = month < 3 ? 'Winter' : month < 6 ? 'Spring' : month < 9 ? 'Summer' : 'Fall';
      
      if (!seasonalData?.[season]) {
        seasonalData[season] = { count: 0, totalSeverity: 0, symptoms: {} };
      }
      
      seasonalData[season].count++;
      seasonalData[season].totalSeverity += entry?.severity || 0;
      
      entry?.symptom_categories?.forEach(symptom => {
        seasonalData[season].symptoms[symptom] = (seasonalData?.[season]?.symptoms?.[symptom] || 0) + 1;
      });
    });

    Object.keys(seasonalData)?.forEach(season => {
      seasonalData[season].avgSeverity = seasonalData?.[season]?.totalSeverity / seasonalData?.[season]?.count;
    });

    return seasonalData;
  };

  const evaluateInterventions = (data, filters) => {
    const timeframeData = filterByTimeframe(data, filters?.timeframe);
    const interventions = [];

    // Medication effectiveness
    const withMedication = timeframeData?.filter(e => e?.medication_status === 'taken');
    const withoutMedication = timeframeData?.filter(e => e?.medication_status === 'not_taken');

    if (withMedication?.length >= 3 && withoutMedication?.length >= 3) {
      const avgWithMed = withMedication?.reduce((sum, e) => sum + (e?.severity || 0), 0) / withMedication?.length;
      const avgWithoutMed = withoutMedication?.reduce((sum, e) => sum + (e?.severity || 0), 0) / withoutMedication?.length;
      
      interventions?.push({
        type: 'medication',
        effectiveness: ((avgWithoutMed - avgWithMed) / avgWithoutMed) * 100,
        confidence: 0.80,
        avgSeverityWith: avgWithMed,
        avgSeverityWithout: avgWithoutMed,
        sampleSize: { with: withMedication?.length, without: withoutMedication?.length }
      });
    }

    return interventions;
  };

  const filterByTimeframe = (data, timeframe) => {
    const now = new Date();
    const cutoffDate = new Date();
    
    switch (timeframe) {
      case '30days':
        cutoffDate?.setDate(now?.getDate() - 30);
        break;
      case '90days':
        cutoffDate?.setDate(now?.getDate() - 90);
        break;
      case '6months':
        cutoffDate?.setMonth(now?.getMonth() - 6);
        break;
      case '1year':
        cutoffDate?.setFullYear(now?.getFullYear() - 1);
        break;
      default:
        return data;
    }

    return data?.filter(entry => new Date(entry?.created_at) >= cutoffDate);
  };

  const calculateTrendInsight = (severityTrend) => {
    if (severityTrend?.length < 2) return 'Insufficient data for trend analysis';
    
    const recent = severityTrend?.slice(-7);
    const older = severityTrend?.slice(0, 7);
    
    const recentAvg = recent?.reduce((sum, e) => sum + e?.severity, 0) / recent?.length;
    const olderAvg = older?.reduce((sum, e) => sum + e?.severity, 0) / older?.length;
    
    const change = ((recentAvg - olderAvg) / olderAvg) * 100;
    
    if (change > 15) return `Severity increasing by ${change?.toFixed(1)}%`;
    if (change < -15) return `Severity decreasing by ${Math.abs(change)?.toFixed(1)}%`;
    return 'Severity stable';
  };

  const handleExportReport = async () => {
    // Export comprehensive analytics report
    const report = {
      generatedAt: new Date()?.toISOString(),
      timeframe: filters?.timeframe,
      analytics: analyticsData,
      rawData: symptomData
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `advanced-analytics-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    a?.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading advanced analytics...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Advanced Symptom Analytics - MyBodyToday</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50 pb-24">
        <BottomTabNavigation />
        
        {/* Header with Close Button */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Go back"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <Brain className="w-6 h-6 text-indigo-600" />
                <h1 className="text-2xl font-bold text-gray-900">Advanced Symptom Analytics</h1>
              </div>
              <div className="flex items-center gap-3">
                <CloseButton navigateTo="/dashboard" onClose={() => navigate('/dashboard')} ariaLabel="Close analytics" />
                <LogoutButton />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Filters */}
          <AnalyticsFilters filters={filters} onFilterChange={setFilters} />

          {/* Panel Navigation */}
          <div className="bg-white rounded-lg shadow-sm p-2 mb-6 flex gap-2 overflow-x-auto">
            {[
              { id: 'patterns', label: 'Pattern Recognition', icon: TrendingUp },
              { id: 'correlations', label: 'Correlations', icon: Target },
              { id: 'predictions', label: 'Predictions', icon: AlertTriangle },
              { id: 'triggers', label: 'Trigger Analysis', icon: Brain },
              { id: 'seasonal', label: 'Seasonal Patterns', icon: Calendar },
              { id: 'interventions', label: 'Interventions', icon: Settings }
            ]?.map(panel => (
              <button
                key={panel?.id}
                onClick={() => setActivePanel(panel?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                  activePanel === panel?.id
                    ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <panel.icon className="w-4 h-4" />
                {panel?.label}
              </button>
            ))}
          </div>

          {/* Analytics Panels */}
          <div className="space-y-6">
            {activePanel === 'patterns' && analyticsData?.patterns && (
              <PatternRecognitionPanel patterns={analyticsData?.patterns} />
            )}
            {activePanel === 'correlations' && analyticsData?.correlations && (
              <CorrelationMatrix correlations={analyticsData?.correlations} />
            )}
            {activePanel === 'predictions' && analyticsData?.predictions && (
              <PredictiveInsights predictions={analyticsData?.predictions} />
            )}
            {activePanel === 'triggers' && analyticsData?.triggers && (
              <TriggerAnalysis triggers={analyticsData?.triggers} />
            )}
            {activePanel === 'seasonal' && analyticsData?.seasonal && (
              <SeasonalCorrelations seasonal={analyticsData?.seasonal} />
            )}
            {activePanel === 'interventions' && analyticsData?.interventions && (
              <InterventionEffectiveness interventions={analyticsData?.interventions} />
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default AdvancedSymptomAnalytics;